import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { AffiliatePicker } from './AffiliatePicker';
import apiFetch from '../../auth/apiFetch';
import { properties } from '../../properties';

function isoToPtBr(iso) {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
}

function groupBySection(positions) {
    const sections = [];
    const seen = {};
    for (const pos of positions) {
        if (!seen[pos.section]) {
            seen[pos.section] = [];
            sections.push({ name: pos.section, positions: seen[pos.section] });
        }
        seen[pos.section].push(pos);
    }
    return sections;
}

export function BoardComposition({ termId }) {
    const [composition, setComposition] = useState(null);
    const [loadError, setLoadError] = useState('');
    const [loading, setLoading] = useState(true);
    const [pickerPositionId, setPickerPositionId] = useState(null);
    const [deleteMember, setDeleteMember] = useState(null);
    const toast = useRef(null);

    useEffect(() => { loadComposition(); }, [termId]); // eslint-disable-line react-hooks/exhaustive-deps

    async function loadComposition() {
        setLoading(true);
        setLoadError('');
        try {
            const res = await apiFetch(
                `${properties.baseURL}${properties.boardTermsPath}/${termId}/composition`
            );
            if (!res.ok) throw new Error(`Erro ${res.status} ao carregar composição`);
            setComposition(await res.json());
        } catch (e) {
            setLoadError(e.message);
        } finally {
            setLoading(false);
        }
    }

    async function addMember(positionId, affiliate) {
        const position = composition.positions.find(p => p.positionId === positionId);
        const maxOrder = position.members.length > 0
            ? Math.max(...position.members.map(m => m.displayOrder))
            : -10;
        const displayOrder = maxOrder + 10;
        try {
            const res = await apiFetch(
                `${properties.baseURL}${properties.boardTermsPath}/${termId}/members`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ positionId, affiliateId: affiliate.id, displayOrder }),
                }
            );
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.error || `Erro ${res.status}`);
            }
            toast.current.show({
                severity: 'success', summary: 'Sucesso',
                detail: `${affiliate.name} adicionado`, life: 3000,
            });
            await loadComposition();
        } catch (e) {
            toast.current.show({ severity: 'error', summary: 'Erro', detail: e.message, life: 5000 });
        }
    }

    async function removeMember() {
        if (!deleteMember) return;
        const { memberId, name } = deleteMember;
        setDeleteMember(null);
        try {
            const res = await apiFetch(
                `${properties.baseURL}${properties.boardMembersPath}/${memberId}`,
                { method: 'DELETE' }
            );
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.error || `Erro ${res.status}`);
            }
            toast.current.show({
                severity: 'success', summary: 'Sucesso',
                detail: `${name} removido`, life: 3000,
            });
            await loadComposition();
        } catch (e) {
            toast.current.show({ severity: 'error', summary: 'Erro', detail: e.message, life: 5000 });
        }
    }

    async function reorderMember(positionId, memberIndex, direction) {
        const position = composition.positions.find(p => p.positionId === positionId);
        if (!position) return;
        const targetIndex = memberIndex + direction;
        if (targetIndex < 0 || targetIndex >= position.members.length) return;

        const memberA = position.members[memberIndex];
        const memberB = position.members[targetIndex];

        // Se os displayOrders são iguais, atribuímos baseado no índice de destino
        let orderA, orderB;
        if (memberA.displayOrder !== memberB.displayOrder) {
            orderA = memberB.displayOrder;
            orderB = memberA.displayOrder;
        } else {
            orderA = targetIndex * 10;
            orderB = memberIndex * 10;
        }

        try {
            for (const [id, ord] of [[memberA.memberId, orderA], [memberB.memberId, orderB]]) {
                const res = await apiFetch(
                    `${properties.baseURL}${properties.boardMembersPath}/${id}`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ displayOrder: ord }),
                    }
                );
                if (!res.ok) {
                    const body = await res.json().catch(() => ({}));
                    throw new Error(body.error || `Erro ${res.status}`);
                }
            }
            await loadComposition();
        } catch (e) {
            toast.current.show({ severity: 'error', summary: 'Erro', detail: e.message, life: 5000 });
        }
    }

    if (loading) {
        return <div className="surface-card p-4 border-round shadow-2 text-500">Carregando composição...</div>;
    }
    if (loadError) {
        return (
            <div className="surface-card p-4 border-round shadow-2">
                <Button icon="pi pi-arrow-left" label="Voltar" className="p-button-text mb-3"
                    onClick={() => window.location.href = '/board/terms'} />
                <Message severity="error" text={loadError} className="w-full" />
            </div>
        );
    }
    if (!composition) return null;

    const sections = groupBySection(composition.positions);

    return (
        <div className="surface-card p-4 border-round shadow-2">
            <Toast ref={toast} />

            {/* Cabeçalho */}
            <div className="flex justify-content-between align-items-start mb-5">
                <div>
                    <Button
                        icon="pi pi-arrow-left"
                        label="Gestões"
                        className="p-button-text p-button-sm mb-2"
                        onClick={() => window.location.href = '/board/terms'}
                    />
                    <div className="text-3xl text-800 font-bold">{composition.description}</div>
                    <div className="text-500 mt-1">
                        {isoToPtBr(composition.startDate)}
                        {composition.endDate
                            ? ` — ${isoToPtBr(composition.endDate)}`
                            : ' — em andamento'}
                    </div>
                </div>
                {!composition.endDate && <Tag severity="success" value="Vigente" className="text-base" />}
            </div>

            {/* Composição agrupada por seção */}
            {sections.length === 0 && (
                <div className="text-400 text-center py-5">
                    Nenhum cargo configurado. Cadastre cargos em <b>Cargos</b> primeiro.
                </div>
            )}

            {sections.map(section => (
                <div key={section.name} className="mb-5">
                    {/* Cabeçalho de seção */}
                    <div className="text-xl font-bold text-700 border-bottom-2 border-300 pb-2 mb-3">
                        {section.name}
                    </div>

                    {section.positions.map(position => (
                        <div key={position.positionId} className="ml-2 mb-4">
                            {/* Nome do cargo + botão adicionar */}
                            <div className="flex justify-content-between align-items-center mb-2">
                                <span className="font-semibold text-800">{position.name}</span>
                                <Button
                                    icon="pi pi-plus"
                                    label="Adicionar"
                                    className="p-button-text p-button-sm"
                                    onClick={() => setPickerPositionId(position.positionId)}
                                />
                            </div>

                            {/* Estado vazio */}
                            {position.members.length === 0 ? (
                                <div
                                    className="flex align-items-center gap-2 text-400 p-2 ml-2"
                                    style={{
                                        border: '1.5px dashed #ced4da',
                                        borderRadius: '6px',
                                    }}
                                >
                                    <i className="pi pi-user" style={{ fontSize: '1rem' }} />
                                    <span className="text-sm">Nenhum membro definido</span>
                                </div>
                            ) : (
                                position.members.map((member, idx) => (
                                    <div
                                        key={member.memberId}
                                        className="flex justify-content-between align-items-center ml-2 mb-1 px-3 py-2"
                                        style={{ background: '#f8f9fa', borderRadius: '6px' }}
                                    >
                                        <span>
                                            <span className="text-400 text-sm mr-2">{member.jkaId}</span>
                                            <span className="font-medium text-800">{member.name}</span>
                                        </span>
                                        <div className="flex gap-1">
                                            <Button
                                                icon="pi pi-angle-up"
                                                className="p-button-rounded p-button-text p-button-sm"
                                                disabled={idx === 0}
                                                tooltip="Mover para cima"
                                                onClick={() => reorderMember(position.positionId, idx, -1)}
                                            />
                                            <Button
                                                icon="pi pi-angle-down"
                                                className="p-button-rounded p-button-text p-button-sm"
                                                disabled={idx === position.members.length - 1}
                                                tooltip="Mover para baixo"
                                                onClick={() => reorderMember(position.positionId, idx, 1)}
                                            />
                                            <Button
                                                icon="pi pi-trash"
                                                className="p-button-rounded p-button-text p-button-danger p-button-sm"
                                                tooltip="Remover"
                                                onClick={() => setDeleteMember({ memberId: member.memberId, name: member.name })}
                                            />
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    ))}
                </div>
            ))}

            {/* Picker de filiado */}
            <AffiliatePicker
                visible={!!pickerPositionId}
                onHide={() => setPickerPositionId(null)}
                onSelect={affiliate => addMember(pickerPositionId, affiliate)}
            />

            {/* Confirmação de remoção de membro */}
            <Dialog
                visible={!!deleteMember}
                style={{ width: '420px' }}
                header="Confirmar remoção"
                modal
                footer={
                    <>
                        <Button label="Não" icon="pi pi-times" className="p-button-text"
                            onClick={() => setDeleteMember(null)} />
                        <Button label="Sim" icon="pi pi-check" className="p-button-text"
                            onClick={removeMember} />
                    </>
                }
                onHide={() => setDeleteMember(null)}
            >
                <div className="flex align-items-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    <span>Remover <b>{deleteMember?.name}</b> deste cargo?</span>
                </div>
            </Dialog>
        </div>
    );
}
